import { useEffect, useState } from "react"

import { Form, Input, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import Icon from "../../Icon"
import ButtonComponent from "../Button"
import PasswordValidator from "../PasswordValidator"

const { Text } = Typography

function NewPasswordAdminForm({ error, ...props }) {
  const navigate = useNavigate()

  const [errorText, setErrorText] = useState(null)

  const [currentError, setCurrentError] = useState({
    text: null,
    error: false
  })

  const [values, setValues] = useState({
    current: null,
    password: null,
    confirm: null
  })

  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    if (error) {
      if (typeof error === "string") {
        if (error.length > 100) {
          setErrorText(t("Server response error"))
        } else {
          setErrorText(error)
        }
      } else {
        if (error?.message?.type === "current_password") {
          setCurrentError({ text: error?.message?.detail, error: true })
        } else {
          setErrorText(Object.values(error)[0])
        }
      }
    }
  }, [error])

  const onFinish = (values) => {
    const data = { ...values }
    props.onSubmit(data)
  }

  const onFinishFailed = ({ errorFields }) => {
    if (errorFields) setErrorText(errorFields[0]["errors"])
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    setErrorText(null)
  }

  function onFocusHandler(e) {
    const target = e.target
    handleClearError(e, target.name)
  }

  function handleChange(e) {
    const { name, value } = e.target
    if (name === "current") {
      handleClearError(e, name)
      setCurrentError({ text: null, error: false })
    }
    setValues({ ...values, [name]: value })
  }

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
    >
      <Form.Item
        name="current"
        label="Current password"
        validateStatus={currentError.error ? "error" : null}
        rules={[{ required: true, message: "Required field" }]}
        style={{ marginBottom: 8 }}
      >
        <Input.Password
          name="current"
          placeholder="Enter your current password..."
          autoComplete="dontshow"
          visibilityToggle
          onChange={(e) => handleChange(e)}
          onFocus={(e) => onFocusHandler(e)}
        />
        {currentError.text && (
          <Space>
            <Icon type={"alert_triangle_error"} style={{ fontSize: 12, color: "#d71313" }} />
            <Text type="danger">{currentError.text || "Test error"}</Text>
          </Space>
        )}
      </Form.Item>

      <Link to="/restore-password" style={{ display: "block", margin: "-4px 0 24px 4px" }}>
        Forgot password?
      </Link>

      <Form.Item
        name="password"
        label="New password"
        validateStatus={errorText ? "error" : null}
        rules={[{ required: true, message: "Required field" }]}
        style={{ marginBottom: 8 }}
      >
        <Input.Password
          name="password"
          placeholder={`${t("New Password")}...`}
          autoComplete="dontshow"
          visibilityToggle
          onChange={(e) => handleChange(e)}
          onFocus={(e) => onFocusHandler(e)}
        />
      </Form.Item>

      <div style={{ marginLeft: 4 }}>
        <PasswordValidator value={values.password} error={errorText} isLogin={true} />
      </div>

      <Form.Item
        name="confirm"
        label="Confirm password"
        style={{ marginBottom: 0 }}
        rules={[
          {
            required: true,
            message: "Please confirm your password!"
          },
          ({ getFieldValue }) => ({
            validator(rule, value) {
              if (!value || !getFieldValue("password") || getFieldValue("password") === value) {
                return value?.length < 8
                  ? Promise.reject(t("Must be at least 8 characters"))
                  : Promise.resolve()
              } else {
                return Promise.reject("Passwords do not match")
              }
            }
          })
        ]}
      >
        <Input.Password
          name="confirm"
          placeholder={`${t("Confirm new password")}...`}
          autoComplete="dontshow"
          visibilityToggle
          onChange={(e) => handleChange(e)}
          onFocus={(e) => onFocusHandler(e)}
        />
      </Form.Item>

      <ButtonComponent
        type="primary"
        htmlType="submit"
        size="large"
        block
        disabled={!values.confirm || !values.current}
      >
        {t("Change password")}
      </ButtonComponent>

      <ButtonComponent
        size="large"
        block
        style={{ marginTop: 10 }}
        onClick={() => navigate("/login")}
      >
        Back to Sign in
      </ButtonComponent>
    </Form>
  )
}

export default NewPasswordAdminForm
